import http from "@services/httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import {
  ContractModel,
  ContractUpdateModel,
} from "@models/contract/contractModel"
import {
  downloadFile,
  resolveExportResponse,
  type IExportQueuedResult,
} from "@lib/helperFile"
import { ContractEventTypeEnum } from "@lib/enum"
import dayjs from "dayjs"

/** Feature-0371 — tham số xuất file sự kiện chuyển đổi từ danh sách hợp đồng đang tick chọn. */
export interface IExportContractEventConvertFromContractInput {
  contractIds: number[]
  /**
   * Chỉ `DA2SPA`, `DA2LTL` hoặc `UpdateSalesPolicy` (Feature-0381) — BE ném lỗi với loại khác.
   */
  eventType: ContractEventTypeEnum
  /** ISO; BE tự format ra chuỗi `dd/MM/yyyy` khi ghi vào tệp. */
  eventDate: string
  configurationPaymentId?: number
}

/**
 * Feature-0379 — tham số xuất lịch thanh toán ĐƯỢC TÍNH TỪ MẪU cho lô hợp đồng đang tick chọn.
 *
 * Khác `exportARContract` (xuất lịch ĐÃ LƯU trên hợp đồng): endpoint này tính lịch tại chỗ từ mẫu
 * `ConfigurationPayment` + dữ liệu hợp đồng và KHÔNG ghi gì xuống DB — tệp chỉ là bản xem trước để
 * sửa tay rồi nạp lại bằng `Imports/ImportARGenerateContract`.
 */
export interface IExportGeneratedPaymentScheduleInput {
  contractIds: number[]
  /** Mẫu lịch thanh toán người dùng chọn; `projectId`/`salesPolicyId` chỉ là bước lọc, KHÔNG gửi lên. */
  configurationPaymentId: number
}

/**
 * Feature-0382 — tệp Word gom bảng lịch thanh toán theo từng hợp đồng.
 *
 * Khác `IExportGeneratedPaymentScheduleInput` (Excel): tệp này là bản ĐỂ ĐỌC / dán vào biểu mẫu,
 * KHÔNG nạp lại được bằng `ImportARGenerateContract`, và endpoint KHÔNG có nhánh chạy nền.
 */
export interface IExportGeneratedPaymentScheduleDocxInput {
  contractIds: number[]
  /** Mẫu lịch thanh toán người dùng chọn; `projectId`/`salesPolicyId` chỉ là bước lọc, KHÔNG gửi lên. */
  configurationPaymentId: number
  /** "vi" | "en" — lựa chọn của người xuất, KHÔNG theo culture giao diện; không truyền ⇒ BE dùng "vi". */
  language?: string
  /** Chuỗi directive `[@TBL@]`/`[@TBLH@]` y cú pháp biểu mẫu in; không truyền ⇒ bố cục mặc định. */
  tableDesign?: string
  /**
   * Lề trái trang, đơn vị **POINT** (1 inch = 72pt, 1cm ≈ 28,35pt) — cùng đơn vị với `rowHeight`/
   * `fontSize` trong `tableDesign`. Không truyền / số âm ⇒ BE giữ mặc định 72pt.
   *
   * Lề hẹp hơn ⇒ vùng in rộng hơn ⇒ bảng 7 cột giãn theo; đây là cách DUY NHẤT làm bảng rộng ra vì
   * `widths` trong `tableDesign` chỉ là trọng số tương đối.
   */
  marginLeft?: number
  /** Lề phải trang, POINT. Xem `marginLeft`. */
  marginRight?: number
}

/** Một mã lịch thanh toán chọn được ở modal (cột 18 của tệp). */
export interface IConfigurationPaymentOption {
  id: number
  code: string
  name: string
}

/** Kết quả tiền kiểm: đếm theo TỪNG lý do bị loại + options dropdown. */
export interface IPrepareExportContractEventConvertResult {
  totalSelected: number
  eligibleCount: number
  /**
   * Bị bỏ vì SAI LOẠI hợp đồng so với loại sự kiện đang chọn. Feature-0381 — nghĩa rộng ra: với
   * `UpdateSalesPolicy` là "không thuộc DA/SPA/LTL", với hai loại chuyển đổi vẫn là "không phải DA".
   * Tên field giữ nguyên theo DTO backend.
   */
  skippedNotDaCount: number
  skippedNoCompanyCount: number
  skippedOutOfScopeCount: number
  paymentScheduleOptions: IConfigurationPaymentOption[]
}

/**
 * Feature-0383 — tham số xuất file sự kiện "Thay đổi chính sách bán hàng" (19 cột salespolicy). KHÔNG có
 * `eventType`: endpoint chỉ phục vụ đúng loại này.
 */
export interface IExportContractEventSalesPolicyFromContractInput {
  contractIds: number[]
  eventDate: string
  configurationPaymentId?: number
}

/**
 * Feature-0383 — kết quả tiền kiểm của luồng salespolicy. Field lệch với convert: "sai loại" ở đây là
 * `skippedUnknownTypeCount` (chỉ loại Unknown bị bỏ, vì loại này nhận cả DA/SPA/LTL).
 */
export interface IPrepareExportContractEventSalesPolicyResult {
  totalSelected: number
  eligibleCount: number
  skippedUnknownTypeCount: number
  skippedNoCompanyCount: number
  skippedOutOfScopeCount: number
  paymentScheduleOptions: IConfigurationPaymentOption[]
}

const getContractAuditFieldLabels = (): Record<string, string> => ({
  ContractTypeId:           L("CONTRACT_DETAIL_LOAI_HOP_DONG"),
  ValidDate:                L("CONTRACT_DETAIL_VALID_DATE"),
  ExpiredDate:              L("CONTRACT_BOOKING_TIME_EXPIRATION"),
  UnitId:                   L("CONTRACT_BOOKING_PRODUCT"),
  CartId:                   L("CONTRACT_BOOKING_CART"),
  AgencyMasterId:           L("CONTRACT_BOOKING_DETAIL_AGENCY_MASTER"),
  StaffId:                  L("CONTRACT_BOOKING_CREATE_NV_PHU_TRACH"),
  SalesPolicyId:            L("CONTRACT_DETAIL_CHINH_SACH_BAN_HANG"),
  Amount:                   L("CONTRACT_CREATE_GIA_SAN_PHAM"),
  AmountAfterDiscounted:    L("CONTRACT_TIEN_CHIEC_KHAU_TRUC_TIEP_8"),
  DiscountAmount:           L("CONTRACT_TIEN_CHIEC_KHAU_THEO_PHAN_TRAM_7"),
  SpecialDiscountAmount:    L("CONTRACT_TIEN_CHIEC_KHAU_SPECIAL_9"),
  MaintenanceAmount:        L("CONTRACT_TIEN_BAO_TRI_13"),
  TotalAmount:              L("CONTRACT_CALENDER_PAYMENT_TOTAL"),
  CalculationDiscount:      L("CONTRACT_BOOKING_CACH_TINH_KM_2"),
  CalculationMethod:        L("PAYMENT_SCHEDULE_CACH_TINH_NGAY_THANH_TOAN"),
  VatTax:                   L("CONTRACT_TIEN_THUE_VAT_11"),
  VatAmount:                L("CONTRACT_BOOKING_THUE_VAT_5"),
  DiscountAmountPercent:    L("CONTRACT_BOOKING_CHUONG_TRINH_KHUYEN_MAI"),
  LandUseFee:               L("UNIT_DETAIL_LAND_USE_FEE"),
  MaintenancePercentage:    L("CONTRACT_BOOKING_PHI_BAO_TRI_4"),
  Status:                   L("CONTRACT_BOOKING_STATUS_PAYMENT"),
  ContractPhase:            L("CONTRACT_DETAIL_PANEL_INFO_CONTRACT"),
  DACode:                   L("CONTRACT_DETAIL_DA_CODE"),
  DADate:                   L("CONTRACT_DETAIL_DA_DATE"),
  DADateActual:             L("CONTRACT_DETAIL_ACTUAL_DA_DATE"),
  SPAOrLTLCode:             L("CONTRACT_EVENT_SPA_CODE"),
  SPAOrLTLDate:             L("CONTRACT_EVENT_SPA_DATE"),
  SPAOrLTLDateActual:       L("CONTRACT_EVENT_ACTUAL_SPA_DATE"),
  ROIDate:                  L("CONTRACT_DETAIL_ROI_DATE"),
  TerminationDate:          L("CONTRACT_DETAIL_TERMINATION_DATE"),
  BookingDate:              L("CONTRACT_BOOKING_TIME_BOOKING"),
  BankLoan:                 L("CONTRACT_DETAIL_BANK_LOAN"),
  BankLoanSubmitDate:       L("CONTRACT_DETAIL_BANK_LOAN_SUBMIT_DATE"),
  BankGuaranteeDate:        L("CONTRACT_DETAIL_BANK_GUARANTEE_DATE"),
  BankGuaranteeAmount:      L("CONTRACT_DETAIL_BANK_GUARANTEE_AMOUNT"),
})

class ContractService {
  public async create(body: any) {
    const cusBody = ContractUpdateModel.assign(body)

    const res = await http.post("api/services/app/Contract/Create", cusBody)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async update(body: any) {
    const cusBody = ContractUpdateModel.assign(body)
    const res = await http.put("api/services/app/Contract/Update", cusBody)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async active(id: number) {
    const res = await http.post(
      "api/services/app/Contract/Active",
      { id }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  public async bulkActive(id: number[]) {
    const res = await http.post("api/services/app/Contract/BulkActive", { ids: id })
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  public async get(id: number): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/Contract/Get", {
      params: { id },
    })
    const { result } = res.data
    return ContractModel.assign(result)
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Contract/GetAll", { params })
    return res.data.result
  }
  public async getARGenerateContract(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/Contract/GetARGenerateContract",
      { params }
    )
    return res.data.result
  }
  public async getAllByCustomer(params: any): Promise<any> {
    const res = await http.get("api/services/app/Contract/GetAllByCustomer", {
      params,
    })
    return res.data.result
  }
  public async getAllForPinkBook(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Contract/GetAllForPinkBook", {
      params,
    })
    return res.data.result
  }
  public async calculateContractValue(body: any) {
    const res = await http.post(
      "api/services/app/Contract/CalculateContract",
      body
    )
    return res.data.result
  }
  public async getAuditLog(id: any) {
    const res = await http.get("api/services/app/Contract/GetAuditLogs", {
      params: { id },
    })
    const labels = getContractAuditFieldLabels()
    return (res.data.result || []).map((row: any) => {
      ;(row.items || []).forEach((item: any) => {
        item.propertyName = labels[item.propertyName] ?? item.propertyName
      })
      return row
    })
  }
  // Generate contract
  public async generateContract(body: any) {
    const res = await http.post(
      "api/services/app/Contract/GenerateContract",
      body
    )
    return res.data.result
  }
  public async getListHistoryGenerateContract(params: any) {
    const res = await http.get(
      "api/services/app/Contract/GetListHistoryGenerateContract",
      {
        params,
      }
    )
    return res.data.result
  }

  // Lưu bản in đã xem trước -> BE tạo record ở Lịch sử in.
  // Không notify ở đây: nơi gọi (modal Xem biểu mẫu) chỉ báo kết quả sau khi tải file về máy.
  public async updateGenerateContract(body: any) {
    const res = await http.put(
      "api/services/app/Contract/UpdateGenerateContract",
      body
    )
    return res.data.result
  }
  // CONTRACT PAYMENT SCHEDULE
  // (Dead methods removed: getPaymentScheduleDetails and createOrUpdateContractPaymentSchedule
  //  called non-existent BE endpoints that have no handler)

  public async getListBookingForContract(params: any): Promise<any[]> {
    const res = await http.get(
      "/api/services/app/Booking/GetListBookingForContract",
      {
        params,
      }
    )
    return res.data.result
  }
  // import/export  booking - contract

  public async exportBookingToContract(params: any): Promise<any> {
    const res = await http.get(
      "api/Export/ExportBookingForImportContractTemplate",

      {
        params,
        responseType: "blob",
      }
    )
    downloadFile(
      res.data,
      `EXPORT-BOOKING-TO-CONTRACT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }


  // import Migration
  public async importMigration(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)

    const result = await http.post(
      "api/Imports/Migration/ImportContract",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )

    return result.data.result
  }

  // import / export paymentSchedule
  public async exportARContract(contractIds: number[]): Promise<any> {
    const res = await http.post("api/Export/ARGenerateContract", contractIds, {
      headers: {
        "content-type": "application/json",
      },
      responseType: "blob",
    })
    downloadFile(
      res.data,
      `EXPORT-AR-GENERATE-CONTRACT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }

  /**
   * Feature-0379 — xuất lịch thanh toán tính từ mẫu cho lô hợp đồng đang tick chọn.
   *
   * Ngưỡng chạy nền của endpoint này rất thấp (mỗi hợp đồng là một lượt tính lịch đầy đủ) nên `202`
   * là đường PHỔ BIẾN chứ không phải ngoại lệ ⇒ bắt buộc đi qua `resolveExportResponse`: request đặt
   * `responseType: "blob"` nên body JSON của 202 cũng về dưới dạng Blob, tải thẳng `res.data` sẽ ra
   * một tệp .xlsx chứa JSON (Excel báo hỏng, không lỗi nào ở console).
   *
   * Trả `null` = đã tải tệp xong; trả object = BE đã queue job, caller phải báo user.
   */
  public async exportGeneratedPaymentSchedule(
    input: IExportGeneratedPaymentScheduleInput
  ): Promise<IExportQueuedResult | null> {
    const res = await http.post(
      "api/Export/ExportGeneratedPaymentSchedule",
      input,
      {
        headers: {
          "content-type": "application/json",
        },
        responseType: "blob",
      }
    )

    return resolveExportResponse(
      res,
      `GENERATED-PAYMENT-SCHEDULE-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
  }

  /**
   * Feature-0382 — xuất tệp Word gom bảng lịch thanh toán của lô hợp đồng đang tick chọn.
   *
   * Khác `exportGeneratedPaymentSchedule` (Excel): endpoint này KHÔNG có nhánh chạy nền, luôn trả
   * `200` + blob .docx ⇒ KHÔNG đi qua `resolveExportResponse`, tải thẳng bằng `downloadFile`.
   */
  public async exportGeneratedPaymentScheduleDocx(
    input: IExportGeneratedPaymentScheduleDocxInput
  ): Promise<void> {
    const res = await http.post(
      "api/Export/ExportGeneratedPaymentScheduleDocx",
      input,
      {
        headers: {
          "content-type": "application/json",
        },
        responseType: "blob",
      }
    )

    downloadFile(
      res.data,
      `GENERATED-PAYMENT-SCHEDULE-${dayjs().format("DDMMYYYYHHmmss")}.docx`
    )
  }

  public async validateBeforeImport(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)

    const result = await http.post(
      "api/Imports/Contract/ValidateBeforeImport",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )

    return result.data.result
  }

  public async importARGenerateContract(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    const result = await http.post(
      "api/Imports/ImportARGenerateContract",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )

    return result.data.result
  }

  public async ImportContractFromBooking(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    const result = await http.post(
      "api/Imports/Contract/ImportContractFromBooking",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
    return result.data.result
  }

  public async exportContractExcel(params: any): Promise<any> {
    const res = await http.get("api/Export/ExportContractList", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-CONTRACT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  /**
   * Feature-0371 — tiền kiểm khi mở modal "Xuất file sự kiện chuyển đổi".
   *
   * Một lời gọi trả CẢ hai thứ modal cần: số dòng sẽ xuất / bị bỏ theo từng lý do, và danh sách mã
   * lịch thanh toán để nạp dropdown. Cố ý KHÔNG chở số dòng bị bỏ qua response header của endpoint
   * export: response đó là blob, và policy CORS của repo khai allow-list cố định
   * (`WithExposedHeaders("Content-Disposition")`) nên header tự thêm bị trình duyệt giấu — FE sẽ đọc
   * `undefined` và báo "0 dòng bị bỏ" mà không có lỗi nào.
   *
   * Feature-0381 — `eventType` là BẮT BUỘC: tập loại hợp đồng hợp lệ khác nhau theo loại sự kiện
   * (`DA2SPA`/`DA2LTL` chỉ nhận HĐ loại DA, `UpdateSalesPolicy` nhận cả DA/SPA/LTL). Gọi lại hàm này
   * mỗi lần người dùng đổi dropdown loại sự kiện, nếu không modal báo một số mà file ra số khác.
   */
  public async prepareExportContractEventConvert(
    contractIds: number[],
    eventType: ContractEventTypeEnum
  ): Promise<IPrepareExportContractEventConvertResult> {
    const res = await http.post(
      "api/services/app/ContractEventConvertFromContractExport/PrepareExport",
      { contractIds, eventType }
    )
    return res.data.result
  }

  /**
   * Feature-0371 — xuất file sự kiện chuyển đổi từ các hợp đồng đang tick chọn.
   *
   * Trả `false` khi BE báo `204 No Content` (không còn hợp đồng hợp lệ). Axios KHÔNG ném lỗi với 204
   * nên bỏ kiểm này sẽ tải về tệp .xlsx 0 byte — Excel báo hỏng mà không toast, không log.
   */
  public async exportContractEventConvertFromContract(
    body: IExportContractEventConvertFromContractInput
  ): Promise<boolean> {
    const res = await http.post(
      "api/Export/ExportContractEventConvertFromContract",
      body,
      { responseType: "blob" }
    )

    if (res.status === 204) return false

    // BE truyền đường dẫn template làm `fileName` của `FileResult` nên tên tệp phải do FE đặt.
    downloadFile(
      res.data,
      `EXPORT-CONTRACT-EVENT-CONVERT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
    notifySuccess(LNotification("SUCCESSFULLY"), LNotification("EXPORT_SUCCESSFULLY"))
    return true
  }

  /**
   * Feature-0383 — tiền kiểm cho luồng xuất "Thay đổi chính sách bán hàng". Endpoint RIÊNG với convert:
   * bố cục 19 cột salespolicy và có bước đổ CTKM ra ô. KHÔNG gửi `eventType`.
   */
  public async prepareExportContractEventSalesPolicy(
    contractIds: number[]
  ): Promise<IPrepareExportContractEventSalesPolicyResult> {
    const res = await http.post(
      "api/services/app/ContractEventSalesPolicyFromContractExport/PrepareExport",
      { contractIds }
    )
    return res.data.result
  }

  /**
   * Feature-0383 — xuất file mồi sự kiện "Thay đổi chính sách bán hàng". Trả `false` khi BE báo `204`.
   */
  public async exportContractEventSalesPolicyFromContract(
    body: IExportContractEventSalesPolicyFromContractInput
  ): Promise<boolean> {
    const res = await http.post(
      "api/Export/ExportContractEventSalesPolicyFromContract",
      body,
      { responseType: "blob" }
    )

    if (res.status === 204) return false

    downloadFile(
      res.data,
      `EXPORT-CONTRACT-EVENT-SALES-POLICY-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
    notifySuccess(LNotification("SUCCESSFULLY"), LNotification("EXPORT_SUCCESSFULLY"))
    return true
  }
}

export default new ContractService()
